import _ from 'lodash';
import * as Constants from "../utils/constants"
import { v4 as uuidv4 } from 'uuid';

export const useEditorFile = () => {
    return {
        findEditorInsertPoint: (editorFile, id) => {
            let found = null;
            const editorPages = getPages(editorFile);
            if (editorPages) {
                editorPages.forEach(editorPage => {
                    editorPage.Items.forEach(editorBaseModel => {
                        const res = findEditorInsertPoint(editorBaseModel, id);
                        if (res) {
                            found = res;
                        }
                    })
                });
            }
            return found;
        },
        getSelectableTexts: (editorFile) => {
            const selectableTexts = [];
            const editorPages = getPages(editorFile);
            if (editorPages) {
                editorPages.forEach(editorPage => {
                    editorPage.Items.forEach(editorBaseModel => {
                        if (editorBaseModel.Type === Constants.editorType.textPart || editorBaseModel.Type === Constants.editorType.textChoice) {
                            selectableTexts.push(editorBaseModel);
                        }
                    })
                });
            }
            return selectableTexts;
        },
        getChildItemsToShow: (editorBaseModel) => {
            return getChildItemsToShow(editorBaseModel);
        },
        getKeyById: (editorBaseModel) => {
            return `${editorBaseModel.Type}${editorBaseModel.Id}`;
        },
        detectLanguageCode: (editorFile) => {
            let languageCode = 'en';
            let languageCodeFromQueryString = detectLanguageCodeFromQueryString();
            if (languageCodeFromQueryString) {
                languageCode = languageCodeFromQueryString;
            } else if (editorFile?.WebEditorSettings?.LanguageCode) {
                languageCode = editorFile?.WebEditorSettings?.LanguageCode;
            }
            return {
                languageCode: languageCode,
                lngFromQueryString: languageCodeFromQueryString,
            }
        },
        findEditorElementById: (editorFile, editorBaseModel, id) => {
            const findById = (item) => {
                return (item.Id === id);
            }
            if (editorFile) {
                return findElementFromEditorFileByFunc(editorFile, findById);
            } else if (editorBaseModel) {
                return findElementByFunc(editorBaseModel, findById);
            }
        },
        findEditorElementByType: (editorFile, editorBaseModel, type) => {
            const findByType = (item) => {
                return (item.Type === type);
            }
            if (editorFile) {
                return findElementFromEditorFileByFunc(editorFile, findByType);
            } else if (editorBaseModel) {
                return findElementByFunc(editorBaseModel, findByType);
            }
        },
        createEmptyEditorText: (editorFile, editorTextTemplate) => {
            const editorText = _.cloneDeep(editorTextTemplate);
            editorFile.DxmlMaxTextId++;
            editorText.Id = editorFile.DxmlMaxTextId.toString();
            editorText.WriteEnabled = true;
            editorText.DxmlContentInputFields = [];

            let emptyContent = "";
            if (editorText.DefaultEditorFontDef) {
                emptyContent = `<span style="font-family: ${editorText.DefaultEditorFontDef.Name}; font-size: ${editorText.DefaultEditorFontDef.Size}">\ufeff</span>`;
            }
            editorText.HtmlContent = `<div><p>${emptyContent}</p></div>`;
            return editorText;
        },
        isAllEditorTableRowsFilled: (editorTable) => {
            return isAllEditorTableRowsFilled(editorTable);
        },
        isEditorTableEmpty: (editorTable) => {
            return isEditorTableEmpty(editorTable);
        },
        isEditorTextEmpty: (editorText) => {
            return isEditorTextEmpty(editorText);
        },
        canAddPageBreakBefore: (editorFile, elementInPage) => {
            return canAddPageBreakBefore(editorFile, elementInPage);
        },
        canAddPageBreakAfter: (editorFile, elementInPage) => {
            return canAddPageBreakAfter(editorFile, elementInPage);
        },
        canRemovePageBreakBefore: (editorFile, elementInPage) => {
            return canRemovePageBreakBefore(editorFile, elementInPage);
        },
        canRemovePageBreakAfter: (editorFile, elementInPage) => {
            return canRemovePageBreakAfter(editorFile, elementInPage);
        },
        addPageBreakBefore: (editorFile, elementInPage) => {
            addPageBreakBefore(editorFile, elementInPage);
        },
        addPageBreakAfter: (editorFile, elementInPage) => {
            addPageBreakAfter(editorFile, elementInPage);
        },
        removePageBreakBefore: (editorFile, elementInPage) => {
            removePageBreakBefore(editorFile, elementInPage);
        },
        removePageBreakAfter: (editorFile, elementInPage) => {
            removePageBreakAfter(editorFile, elementInPage);
        },
        removePageBreak: (editorFile, editorPage) => {
            removePageBreak(editorFile, editorPage);
        },
        getPages: (editorFile) => {
            return getPages(editorFile);
        },
    }
}

function getPages(editorFile) {
    return editorFile?.Doc?.Items?.filter(item => item.Type === Constants.editorType.page);
}

function findElementFromEditorFileByFunc(editorFile, findFunc) {
    const editorPages = getPages(editorFile);
    if (!editorPages)
        return;

    let element;
    for (const editorPage of editorPages) {
        element = findElementByFunc(editorPage, findFunc);
        if (element)
            break;
    }

    return element;
}

function findElementByFunc(editorBaseModel, findFunc) {
    if (!editorBaseModel)
        return;

    if (findFunc(editorBaseModel))
        return editorBaseModel;

    if (!editorBaseModel.Items || editorBaseModel.Items.length === 0)
        return;

    for (const item of editorBaseModel.Items) {
        if (findFunc(item))
            return item;

        switch (item.Type) {
            case Constants.editorType.table:
            case Constants.editorType.tableRow:
            case Constants.editorType.tableColumn:
            case Constants.editorType.breakTable:
            case Constants.editorType.breakTableBody:
            case Constants.editorType.textPart:
            case Constants.editorType.textChoice:
                const found = findElementByFunc(item, findFunc);
                if (found)
                    return found;
                break;
            default:
                break;
        }
    }
}

function isEditorTextEmpty(editorText) {
    if (!editorText)
        return true;

    if (editorText.WriteEnabled) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = editorText.HtmlContent;
        const text = tempDiv.textContent.trim();
        return text.length === 0;
    } else {
        if (!editorText.Items || editorText.Items.length === 0)
            return true;
        if (editorText.HasList)
            return false;
        const allParagraphsEmpty = editorText.Items.every(paragraph => {
            if (!paragraph.Items || paragraph.Items.length === 0)
                return true;
            return false;
        })
        return allParagraphsEmpty;
    }
}

function isEditorTextFilled(editorText) {
    if (!editorText)
        return false;

    if (editorText.WriteEnabled) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = editorText.HtmlContent;
        const text = tempDiv.textContent.trim();
        return text.length > 0;
    } else {
        return editorText.Items?.length > 0;
    }
}

function isEditorTableColumnFilled(editorTableColumn) {
    if (!editorTableColumn.Items || editorTableColumn.Items.length === 0)
        return true;

    const editorTexts = editorTableColumn.Items.filter(x => x.Type === Constants.editorType.text);
    if (!editorTexts || editorTexts.length === 0)
        return true;

    return editorTexts.some(x => isEditorTextFilled(x));
}

function isAllEditorTableColumnsFilled(editorTableRow) {
    if (!editorTableRow.Items || editorTableRow.Items.length === 0)
        return true;

    return editorTableRow.Items.every(column => isEditorTableColumnFilled(column));
}

function isAllEditorTableRowsFilled(editorTable) {
    if (!editorTable.Items || editorTable.Items.length === 0)
        return true;

    return editorTable.Items.every(row => isAllEditorTableColumnsFilled(row));
}

function isEditorTableColumnEmpty(editorTableColumn) {
    if (!editorTableColumn.Items || editorTableColumn.Items.length === 0)
        return true;

    const editorTexts = editorTableColumn.Items.filter(x => x.Type === Constants.editorType.text);
    if (!editorTexts || editorTexts.length === 0)
        return true;

    return editorTexts.every(x => !isEditorTextFilled(x));
}

function isEditorTableRowEmpty(editorTableRow) {
    if (!editorTableRow.Items || editorTableRow.Items.length === 0)
        return true;

    return editorTableRow.Items.every(column => isEditorTableColumnEmpty(column));
}

function isEditorTableEmpty(editorTable) {
    if (!editorTable.Items || editorTable.Items.length === 0)
        return true;

    return editorTable.Items.every(row => isEditorTableRowEmpty(row));
}

function detectLanguageCodeFromQueryString() {
    let found;
    if (typeof window !== 'undefined') {
        let search = window.location.search;
        if (!window.location.search && window.location.hash && window.location.hash.indexOf('?') > -1) {
            search = window.location.hash.substring(window.location.hash.indexOf('?'));
        }
        let query = search.substring(1);
        let params = query.split('&');
        for (let i = 0; i < params.length; i++) {
            let pos = params[i].indexOf('=');
            if (pos > 0) {
                let key = params[i].substring(0, pos);
                if (key === 'lng') {
                    found = params[i].substring(pos + 1);
                }
            }
        }
    }
    return found;
}

function findEditorInsertPoint(editorBaseModel, id) {
    let found = null;
    if (editorBaseModel.Type === Constants.editorType.insertPoint && editorBaseModel.Id === id) {
        return editorBaseModel;
    } else if (editorBaseModel.Type === Constants.editorType.table) {
        editorBaseModel.Items.forEach(editorTableRow => {
            editorTableRow.Items.forEach(editorTableColumn => {
                editorTableColumn.Items.forEach(tableColumnElement => {
                    const res = findEditorInsertPoint(tableColumnElement, id);
                    if (res) {
                        found = res;
                    }
                })
            })
        })
    } else if (editorBaseModel.Type === Constants.editorType.textChoice) {
        editorBaseModel.Items.forEach(editorTextPart => {
            editorTextPart.Items.forEach(textPartElement => {
                const res = findEditorInsertPoint(textPartElement, id);
                if (res) {
                    found = res;
                }
            })
        })
    } else if (editorBaseModel.Type === Constants.editorType.textPart) {
        editorBaseModel.Items.forEach(textPartElement => {
            const res = findEditorInsertPoint(textPartElement, id);
            if (res) {
                found = res;
            }
        })
    }
    return found;
}

function getChildItemsToShow(editorBaseModel) {
    const items = editorBaseModel.Items?.filter((x) =>
        (x.Type === Constants.editorType.text && (x.WriteEnabled || !isEditorTextEmpty(x))) ||
        x.Type === Constants.editorType.table ||
        x.Type === Constants.editorType.breakTable ||
        x.Type === Constants.editorType.textPart ||
        x.Type === Constants.editorType.textChoice ||
        x.Type === Constants.editorType.image ||
        x.Type === Constants.editorType.insertPoint ||
        x.Type === Constants.editorType.svg);

    return items;
}

function findElementAndPageIndex(docElements, elementInPage) {
    let pageIndex;
    let elementIndex;
    for (let i = 0; i < docElements.length; i++) {
        if (docElements[i].Type !== Constants.editorType.page)
            continue;

        elementIndex = docElements[i].Items.findIndex(x => x.Id === elementInPage.Id);
        if (elementIndex >= 0) {
            pageIndex = i;
            break;
        }
    }
    return { pageIndex, elementIndex };
}

function canAddPageBreakBefore(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length)
        return false;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0)
        return false;

    const currentPage = docElements[pageIndex];
    const elementsToShow = getChildItemsToShow(currentPage);
    if (!elementsToShow?.length)
        return false;

    // Check if the element is the first element of the page
    if (elementsToShow[0].Id === elementInPage.Id)
        return false;
    return true;
}

function canAddPageBreakAfter(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length)
        return false;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0)
        return false;

    const currentPage = docElements[pageIndex];
    const elementsToShow = getChildItemsToShow(currentPage);
    if (!elementsToShow?.length)
        return false;

    // Check if the element is the last element of the page
    if (elementsToShow.at(-1)?.Id === elementInPage.Id)
        return false;
    return true;
}

function canRemovePageBreakBefore(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length)
        return false;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0)
        return false;

    const currentPage = docElements[pageIndex];
    const elementsToShow = getChildItemsToShow(currentPage);
    if (!elementsToShow?.length)
        return false;

    // Check if the element is the first element of the page
    if (elementsToShow[0].Id !== elementInPage.Id)
        return false;

    const firstPage = docElements.find(x => x.Type === Constants.editorType.page);
    if (!firstPage)
        return false;
    return (currentPage.Id !== firstPage.Id);
}

function canRemovePageBreakAfter(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length)
        return false;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0)
        return false;

    const currentPage = docElements[pageIndex];
    const elementsToShow = getChildItemsToShow(currentPage);
    if (!elementsToShow?.length)
        return false;

    // Check if the element is the last element of the page
    if (elementsToShow.at(-1)?.Id !== elementInPage.Id)
        return false;

    const lastPage = docElements.findLast(x => x.Type === Constants.editorType.page);
    if (!lastPage)
        return false;
    return (currentPage.Id !== lastPage.Id)
}

function createNewEditorPage(editorPageTemplate) {
    // <Page Sect="1" StartOnOdd="0">
    const newPage = _.cloneDeep(editorPageTemplate);
    newPage.Id = `Page${uuidv4()}`;
    newPage.DxmlAttributes = {
        Sect: editorPageTemplate.DxmlAttributes?.Sect ?? "1",
        StartOnOdd: "0",
    };
    newPage.Items = [];
    return newPage;
}

function addPageBreakBefore(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length) return;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0) return;

    const currentPage = docElements[pageIndex];
    const newPage = createNewEditorPage(currentPage);

    // insert a new page after the current page which the element is inside
    docElements.splice(pageIndex + 1, 0, newPage);

    // insert the current element and all the elements following it into the new page
    for (let i = elementIndex; i < currentPage.Items?.length; i++) {
        newPage.Items.push(currentPage.Items[i]);
    }

    // remove the current element and all the elements following it from the current page
    currentPage.Items.splice(elementIndex, (currentPage.Items.length - elementIndex));
}

function addPageBreakAfter(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length) return;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0) return;

    const currentPage = docElements[pageIndex];
    const newPage = createNewEditorPage(currentPage);

    // insert a new page after the current page which the element is inside
    docElements.splice(pageIndex + 1, 0, newPage);

    // insert all the elements after the current element into the new page
    for (let i = elementIndex + 1; i < currentPage.Items.length; i++) {
        newPage.Items.push(currentPage.Items[i]);
    }

    // remove the current element and all the elements following it from the current page
    currentPage.Items.splice(elementIndex + 1, (currentPage.Items.length - elementIndex - 1));
}

function updateSectionIndex(docElements, currentPage) {
    const currentIndex = docElements.findIndex(x => x.Id === currentPage.Id);
    if (currentIndex < 0) return;

    const nextIndex = docElements.findIndex((x, index) => index > currentIndex && x.Type === Constants.editorType.page);
    if (nextIndex < 0) return;

    const currentSect = Number(docElements[currentIndex].DxmlAttributes?.Sect);
    if (currentSect < 0) return;

    const nextSect = Number(docElements[nextIndex].DxmlAttributes?.Sect);
    if (nextSect < 0) return;

    if ((currentSect + 1) < nextSect) {
        const updatedElements = docElements.filter((x, index) => index >= nextIndex && x.Type === Constants.editorType.page && x.hasOwnProperty("DxmlAttributes"));
        let sectionNumber = currentSect + 1;
        let prevSectionNumber = Number(updatedElements[0].DxmlAttributes.Sect);
        updatedElements[0].DxmlAttributes.Sect = sectionNumber.toString();
        for (let i = 1; i < updatedElements.length; i++) {
            if (updatedElements[i].DxmlAttributes.Sect != prevSectionNumber.toString())
                sectionNumber++;
            prevSectionNumber = Number(updatedElements[i].DxmlAttributes.Sect);
            updatedElements[i].DxmlAttributes.Sect = sectionNumber.toString();
        }
    }
}

function removePageBreakBefore(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length) return;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0) return;

    const currentPage = docElements[pageIndex];
    const prevPage = docElements.filter((x, index) => index < pageIndex).findLast(x => x.Type === Constants.editorType.page);
    if (!prevPage) return;

    // move the elements of the current page to the previous page
    for (const item of currentPage.Items) {
        prevPage.Items.push(item);
    }

    // delete the current page
    docElements.splice(pageIndex, 1);

    updateSectionIndex(docElements, prevPage);
}

function removePageBreakAfter(editorFile, elementInPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length) return;

    const { pageIndex, elementIndex } = findElementAndPageIndex(docElements, elementInPage);
    if (pageIndex < 0 || elementIndex < 0) return;

    const currentPage = docElements[pageIndex];
    const nextPageIndex = docElements.findIndex((x, index) => index > pageIndex && x.Type === Constants.editorType.page);
    if (nextPageIndex < 0) return;

    // move the elements of the next page to the current page
    for (const item of docElements[nextPageIndex].Items) {
        currentPage.Items.push(item);
    }

    // delete the current page
    docElements.splice(nextPageIndex, 1);

    updateSectionIndex(docElements, currentPage);
}

function removePageBreak(editorFile, editorPage) {
    const docElements = editorFile?.Doc?.Items;
    if (!docElements?.length) return;

    const pageIndex = docElements.findIndex(x => x.Id === editorPage.Id);
    if (pageIndex < 0) return;

    const lastPageIndex = docElements.findLastIndex(x => x.Type === Constants.editorType.page);
    if (pageIndex === lastPageIndex) return;

    const currentPage = docElements[pageIndex];
    const nextPageIndex = docElements.findIndex((x, index) => index > pageIndex && x.Type === Constants.editorType.page);
    if (nextPageIndex < 0) return;

    // move the elements of the next page to the current page
    for (const item of docElements[nextPageIndex].Items) {
        currentPage.Items.push(item);
    }

    // delete the current page
    docElements.splice(nextPageIndex, 1);

    updateSectionIndex(docElements, currentPage);
}